import { useFormContext } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ConsultantHoursTransactionForm } from '@/schemas/consultant-hours';

interface ConsultantHoursTransactionFieldsProps {
  /** Hint shown under the hours field for debits (e.g. the hours available). */
  debitHint?: string;
  /** Locks type and hours, e.g. for a Stripe purchase whose amount matches a real payment. */
  lockAmount?: boolean;
}

/**
 * The consultant hours ledger entry fields (type, hours, description), shared by the record and
 * edit dialogs. Must be rendered inside a react-hook-form FormProvider for
 * {@link ConsultantHoursTransactionForm}.
 */
export function ConsultantHoursTransactionFields({
  debitHint,
  lockAmount = false,
}: ConsultantHoursTransactionFieldsProps) {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<ConsultantHoursTransactionForm>();

  const type = watch('type');

  return (
    <>
      <div className="grid gap-2">
        <Label htmlFor="consultantHoursType">Type</Label>
        <Select
          value={type}
          disabled={lockAmount}
          onValueChange={(value) =>
            setValue('type', value as ConsultantHoursTransactionForm['type'])
          }
        >
          <SelectTrigger id="consultantHoursType">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Credit">Credit (add hours)</SelectItem>
            <SelectItem value="Debit">Debit (consume hours)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-2">
        <Label htmlFor="consultantHours">Hours</Label>
        <Input
          id="consultantHours"
          type="number"
          step="0.01"
          min="0.01"
          disabled={lockAmount}
          {...register('hours', { valueAsNumber: true })}
        />
        {errors.hours ? (
          <p className="text-sm text-destructive">{errors.hours.message}</p>
        ) : lockAmount ? (
          <p className="text-sm text-muted-foreground">
            Stripe purchases keep the hours and type that were paid for.
          </p>
        ) : (
          type === 'Debit' &&
          debitHint && <p className="text-sm text-muted-foreground">{debitHint}</p>
        )}
      </div>
      <div className="grid gap-2">
        <Label htmlFor="consultantHoursDescription">Description (optional)</Label>
        <Input
          id="consultantHoursDescription"
          type="text"
          maxLength={1000}
          placeholder={type === 'Credit' ? 'Complimentary hours' : 'Strategy workshop'}
          {...register('description')}
        />
        {errors.description && (
          <p className="text-sm text-destructive">{errors.description.message}</p>
        )}
      </div>
    </>
  );
}
